import { buildingCRUD } from "../../actions/building";
import { boundaryCRUD } from "../../actions/boundary";
import { selector, building as buildingApi } from "../../api";
import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import Select from "react-select";

export const UploadBuilding = (props) => {
  const { setIndex, index } = props;

  const user = selector.User();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [boundaries, setBoundaries] = useState([]);
  const [selectedBoundary, setSelectedBoundary] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [step, setStep] = useState(1); // 1: Select boundary & file, 2: Preview

  // Load boundaries (only Locality type - Shehia)
  useEffect(() => {
    boundaryCRUD
      .loadBoundary(1000, 0, "Locality", "")
      .then((resp) => {
        // Handle both array response and paginated response with results
        const data = Array.isArray(resp.data) ? resp.data : (resp.data.results || []);
        const options = data.map((item) => ({
          value: item.id,
          label: `${item.boundary_name} (${item.boundary_code || "N/A"})`,
        }));
        setBoundaries(options);
      })
      .catch((error) => {
        console.error("Failed to load boundaries:", error);
      });
  }, []);

  const parseCSV = (text) => {
    const lines = text.split("\n").filter((line) => line.trim());
    if (lines.length === 0) return [];

    const headers = lines[0].split(",").map((h) => h.trim().replace(/^"|"$/g, ""));
    const data = [];

    for (let i = 1; i < lines.length; i++) {
      const values = [];
      let current = "";
      let inQuotes = false;

      for (let char of lines[i]) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === "," && !inQuotes) {
          values.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const row = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || "";
      });
      data.push(row);
    }

    return data;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setPreviewData([]);
      setShowPreview(false);
    }
  };

  const handlePreview = () => {
    if (!file) {
      toast.error("Please select a CSV file first", { autoClose: 5000 });
      return;
    }

    if (!selectedBoundary) {
      toast.error("Please select a Shehia first", { autoClose: 5000 });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const data = parseCSV(text);
      setPreviewData(data);
      setShowPreview(true);
      setStep(2);
    };
    reader.readAsText(file);
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!file) {
      toast.error("Please select a CSV file", { autoClose: 5000 });
      return;
    }

    if (!selectedBoundary) {
      toast.error("Please select a Shehia", { autoClose: 5000 });
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("recorded_by", user.id);
    formData.append("boundary_id", selectedBoundary.value);

    setLoading(true);
    buildingCRUD
      .uploadBuilding(formData)
      .then((resp) => {
        toast.success(resp.data.message || "Buildings uploaded successfully", {
          autoClose: 7000,
        });
        if (resp.data.total_errors > 0) {
          toast.warning(`${resp.data.total_errors} rows had errors`, {
            autoClose: 10000,
          });
        }
        setIndex(index + 1);
        resetForm();
      })
      .catch((error) => {
        toast.error(error.response?.data?.error || "Failed to upload file", {
          autoClose: 7000,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const resetForm = () => {
    setFile(null);
    setFileName("");
    setSelectedBoundary(null);
    setPreviewData([]);
    setShowPreview(false);
    setStep(1);
    const fileInput = document.getElementById("buildingCsvFile");
    if (fileInput) fileInput.value = "";
  };

  const downloadTemplate = () => {
    window.open(`${buildingApi}/building/upload/template`, "_blank");
  };

  // Get all columns from the first row of preview data (dynamic columns)
  const previewColumns = previewData.length > 0 ? Object.keys(previewData[0]) : [];

  return (
    <>
      <div
        className="modal fade uploadModal"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="uploadModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title" id="uploadModalLabel">
                Upload Buildings from CSV
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={resetForm}
              ></button>
            </div>
            <hr />

            {/* Step Indicator */}
            <div className="px-4 pt-2">
              <div className="d-flex justify-content-center mb-3">
                <div className="d-flex align-items-center">
                  <span
                    className={`badge rounded-pill ${
                      step >= 1 ? "bg-primary" : "bg-secondary"
                    } me-2`}
                    style={{ width: "28px", height: "28px", lineHeight: "20px" }}
                  >
                    1
                  </span>
                  <span className={step >= 1 ? "fw-bold" : ""}>Select</span>
                  <div
                    className={`mx-3 ${step >= 2 ? "bg-primary" : "bg-secondary"}`}
                    style={{ width: "50px", height: "2px" }}
                  ></div>
                  <span
                    className={`badge rounded-pill ${
                      step >= 2 ? "bg-primary" : "bg-secondary"
                    } me-2`}
                    style={{ width: "28px", height: "28px", lineHeight: "20px" }}
                  >
                    2
                  </span>
                  <span className={step >= 2 ? "fw-bold" : ""}>Preview & Upload</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Step 1: Select Boundary and File */}
                {step === 1 && (
                  <>
                    <div className="row mb-4">
                      <div className="col-12">
                        <div className="alert alert-info">
                          <div className="d-flex justify-content-between align-items-start">
                            <div>
                              <h6 className="alert-heading">
                                CSV Format Instructions:
                              </h6>
                              <p className="mb-2">
                                Your CSV file should have the following columns:
                              </p>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary"
                              onClick={downloadTemplate}
                            >
                              <i className="ri-download-line me-1"></i> Download
                              Template
                            </button>
                          </div>
                          <div className="table-responsive" style={{ maxHeight: "200px", overflowY: "auto" }}>
                            <table className="table table-sm table-bordered mb-2">
                              <thead className="table-light sticky-top">
                                <tr>
                                  <th>Column</th>
                                  <th>Description</th>
                                  <th>Required</th>
                                  <th>Example</th>
                                </tr>
                              </thead>
                              <tbody>
                                <tr>
                                  <td><code>name</code></td>
                                  <td>Building name</td>
                                  <td>No</td>
                                  <td>Silima Juma Kidawa</td>
                                </tr>
                                <tr>
                                  <td><code>initials</code></td>
                                  <td>Building initials</td>
                                  <td>No</td>
                                  <td>SJK</td>
                                </tr>
                                <tr>
                                  <td><code>code</code></td>
                                  <td>Building code</td>
                                  <td>No</td>
                                  <td>106110307</td>
                                </tr>
                                <tr>
                                  <td><code>brn</code></td>
                                  <td>Building Registration Number</td>
                                  <td>No</td>
                                  <td>338</td>
                                </tr>
                                <tr>
                                  <td><code>postcode</code></td>
                                  <td>Postal code</td>
                                  <td>No</td>
                                  <td>71112</td>
                                </tr>
                                <tr>
                                  <td><code>house_no</code></td>
                                  <td>House number</td>
                                  <td>No</td>
                                  <td>330</td>
                                </tr>
                                <tr>
                                  <td><code>elecricity_meter_no</code></td>
                                  <td>Electricity meter number</td>
                                  <td>No</td>
                                  <td>2.2182E+10</td>
                                </tr>
                                <tr>
                                  <td><code>water_meter_no</code></td>
                                  <td>Water meter number</td>
                                  <td>No</td>
                                  <td>1</td>
                                </tr>
                                <tr>
                                  <td><code>number_of_units</code></td>
                                  <td>Number of units in building</td>
                                  <td>No</td>
                                  <td>1</td>
                                </tr>
                                <tr>
                                  <td><code>number_of_floors</code></td>
                                  <td>Number of floors</td>
                                  <td>No</td>
                                  <td>1</td>
                                </tr>
                                <tr>
                                  <td><code>is_condominium</code></td>
                                  <td>Is it a condominium?</td>
                                  <td>No</td>
                                  <td>FALSE, TRUE, 0, 1, yes, no</td>
                                </tr>
                                <tr>
                                  <td><code>is_verified</code></td>
                                  <td>Is the building verified?</td>
                                  <td>No</td>
                                  <td>TRUE, FALSE, 0, 1, yes, no</td>
                                </tr>
                                <tr>
                                  <td><code>zone_id</code></td>
                                  <td>Zone ID the building belongs to (kept alongside the Shehia)</td>
                                  <td>No</td>
                                  <td>12</td>
                                </tr>
                                <tr>
                                  <td><code>street_id</code></td>
                                  <td>Street / Road ID (optional)</td>
                                  <td>No</td>
                                  <td>25</td>
                                </tr>
                                <tr>
                                  <td><code>dictionary_building_category</code></td>
                                  <td>Building category ID (Residential=25, Commercial=26, etc.)</td>
                                  <td>No</td>
                                  <td>25</td>
                                </tr>
                                <tr>
                                  <td><code>dictionary_building_type</code></td>
                                  <td>Building type ID</td>
                                  <td>No</td>
                                  <td></td>
                                </tr>
                                <tr>
                                  <td><code>building_centroid</code></td>
                                  <td>Point geometry (WKT format)</td>
                                  <td>No</td>
                                  <td>POINT(39.217 -6.143)</td>
                                </tr>
                                <tr>
                                  <td><code>building_polygon</code></td>
                                  <td>MultiPolygon geometry (WKT format)</td>
                                  <td>No</td>
                                  <td>MULTIPOLYGON(((39.21... -6.14...)))</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                          <p className="mb-0 small">
                            <strong>Note:</strong> UUID is auto-generated.
                            Geometry fields should be in WKT (Well-Known Text)
                            format. The <code>boundary_id</code> will be set from
                            your selection below.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Select Shehia{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <Select
                            options={boundaries}
                            value={selectedBoundary}
                            onChange={setSelectedBoundary}
                            placeholder="Search and select Shehia..."
                            isClearable
                            isSearchable
                          />
                          {selectedBoundary && (
                            <small className="text-success mt-1 d-block">
                              <i className="ri-check-line"></i> Selected:{" "}
                              {selectedBoundary.label}
                            </small>
                          )}
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="mb-3">
                          <label className="form-label">
                            Select CSV File <span className="text-danger">*</span>
                          </label>
                          <input
                            type="file"
                            className="form-control"
                            id="buildingCsvFile"
                            accept=".csv"
                            onChange={handleFileChange}
                          />
                          {fileName && (
                            <small className="text-muted mt-1 d-block">
                              Selected: {fileName}
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {/* Step 2: Preview Data */}
                {step === 2 && showPreview && (
                  <>
                    <div className="alert alert-success mb-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <strong>
                            <i className="ri-file-list-line me-1"></i> File:{" "}
                          </strong>
                          {fileName}
                          <span className="ms-3">
                            <strong>
                              <i className="ri-map-pin-line me-1"></i> Shehia:{" "}
                            </strong>
                            {selectedBoundary?.label}
                          </span>
                          <span className="ms-3">
                            <strong>
                              <i className="ri-database-line me-1"></i> Total
                              Rows:{" "}
                            </strong>
                            {previewData.length}
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => setStep(1)}
                        >
                          <i className="ri-arrow-left-line me-1"></i> Back to
                          Selection
                        </button>
                      </div>
                    </div>

                    <div
                      className="table-responsive"
                      style={{
                        maxHeight: "400px",
                        overflowX: "auto",
                        overflowY: "auto"
                      }}
                    >
                      <table
                        className="table table-sm table-bordered table-striped table-hover"
                        style={{ minWidth: `${Math.max(800, previewColumns.length * 120)}px` }}
                      >
                        <thead className="table-light sticky-top" style={{ top: 0, zIndex: 1 }}>
                          <tr>
                            <th style={{ minWidth: "50px", position: "sticky", left: 0, background: "#f8f9fa", zIndex: 2 }}>#</th>
                            {previewColumns.map((col) => (
                              <th key={col} style={{ minWidth: "100px", whiteSpace: "nowrap" }}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.map((row, idx) => (
                            <tr key={idx}>
                              <td style={{ position: "sticky", left: 0, background: "#fff", zIndex: 1 }}>{idx + 1}</td>
                              {previewColumns.map((col) => (
                                <td key={col} style={{ whiteSpace: "nowrap", maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis" }}>
                                  {row[col] || "-"}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    {previewData.length === 0 && (
                      <div className="alert alert-warning">
                        No data found in the CSV file. Please check the file
                        format.
                      </div>
                    )}
                  </>
                )}
              </div>

              <hr />
              <div className="modal-footer">
                <a
                  href="javascript:void(0);"
                  className="btn btn-soft-danger fw-medium"
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                >
                  <i className="ri-close-line me-1 align-middle"></i> Close
                </a>

                {step === 1 && (
                  <button
                    type="button"
                    className="btn btn-soft-primary"
                    onClick={handlePreview}
                    disabled={!file || !selectedBoundary}
                  >
                    <i className="ri-eye-line me-1"></i> Preview Data
                  </button>
                )}

                {step === 2 && (
                  <>
                    <button
                      type="button"
                      className="btn btn-soft-secondary"
                      onClick={() => setStep(1)}
                    >
                      <i className="ri-arrow-left-line me-1"></i> Back
                    </button>
                    <button
                      type="submit"
                      className="btn btn-soft-success"
                      disabled={loading || previewData.length === 0}
                    >
                      {!loading && (
                        <span className="indicator-label">
                          <i className="las la-upload la-lg"></i> Upload{" "}
                          {previewData.length} Buildings
                        </span>
                      )}
                      {loading && (
                        <span
                          className="indicator-progress"
                          style={{ display: "block" }}
                        >
                          Uploading...
                          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                        </span>
                      )}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};
